/*
 * Mirrors the rendered state of Shopify's cookie-consent UI onto <html> as a
 * `consent-banner-visible` class, so stylesheets can react to it.
 *
 * Needed because CSS can't tell "showing" from "dismissed" on its own: the
 * banner (#shopify-pc__banner) and its preferences modal (#shopify-pc__prefs)
 * are injected asynchronously as direct <body> children, and Shopify shows and
 * hides them by writing an inline display value. Neither is ever removed from
 * the DOM, so a :has() test for their presence would stay true forever.
 *
 * Fails open by design: if the script never runs, the dialogs never appear, or
 * the observers never fire, the class is simply never set and everything keeps
 * its normal styling.
 */
(() => {
  const DIALOG_IDS = ['shopify-pc__banner', 'shopify-pc__prefs'];
  const VISIBLE_CLASS = 'consent-banner-visible';

  const observed = new WeakSet();

  // Both dialogs are position:fixed, which makes offsetParent permanently null,
  // so measure boxes instead — display:none yields no client rects.
  const isRendered = (element) => element !== null && element.getClientRects().length > 0;

  // Called straight from the observers rather than off a rAF: rAF is throttled
  // to a standstill in a background tab, which would leave the class stale.
  // MutationObserver already batches its callbacks per microtask checkpoint.
  const sync = () => {
    const visible = DIALOG_IDS.some((id) => isRendered(document.getElementById(id)));
    document.documentElement.classList.toggle(VISIBLE_CLASS, visible);
  };

  // Watch each dialog's inline style once it lands in the DOM. The preferences
  // modal is only built the first time it is opened, so this runs on every
  // <body> child change rather than just at startup.
  const observeDialogs = () => {
    DIALOG_IDS.forEach((id) => {
      const dialog = document.getElementById(id);
      if (dialog === null || observed.has(dialog)) return;

      observed.add(dialog);
      new MutationObserver(sync).observe(dialog, { attributes: true, attributeFilter: ['style', 'class'] });
    });

    sync();
  };

  const start = () => {
    new MutationObserver(observeDialogs).observe(document.body, { childList: true });
    document.addEventListener('visitorConsentCollected', sync);
    observeDialogs();
  };

  if (document.body === null) {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
